import * as React from 'react';
import _ from 'lodash';

import { EnhancedInput } from 'common/components/core/input';
import { Select, SelectTypes } from 'common/components/core/select';
import { FieldTypeEnum } from 'common/enums/FieldTypeEnum';

import Switch from 'common/components/core/switch';
import popOverFactory from 'common/components/core/popover/popOverFactory';
import { memoized } from 'common/utils/Memoizer';

const SelectWithPopover = popOverFactory({
    withObserverFlag: true,
    Component: Select
});

class BaseField extends React.PureComponent {
    onChange = (value, fieldIndex) => {
        const { onChange, field } = this.props;
        onChange && onChange({ field, value, fieldIndex });
    };

    onBlur = () => {
        const { onBlur, field } = this.props;
        onBlur && onBlur({ field });
    };

    onDelete = fieldIndex => {
        const { onDelete, field } = this.props;
        onDelete && onDelete({ field, fieldIndex });
    };

    getFieldProps = ({ field }) => {
        const { getFieldProps } = this.props;
        return (
            (getFieldProps && getFieldProps({ field })) ||
            field.defaultProps({ field })
        );
    };

    @memoized()
    getSelectValues(value) {
        let selectValues = value;
        if (typeof value !== 'string' && value && value.length) {
            selectValues = value.map(val => {
                return val.id;
            });
        }
        return selectValues;
    }

    renderSelect = ({ disabled, allProps, onBlur }) => {
        const { field, isEditing, t, theme } = this.props;
        const selectValues = this.getSelectValues(field.value);
        return (
            //@others-spread-end
            <SelectWithPopover
                maxMenuHeight={190}
                theme={theme}
                noMarginBottom
                disabled={isEditing && disabled}
                selectType={SelectTypes.Outline}
                options={
                    _.isFunction(field.options)
                        ? field.options({ t, props: allProps.props, field })
                        : field.options
                }
                openMenuOnFocus
                blurInputOnSelect={false}
                isSearchable={false}
                isClearable={!field.required}
                placeholder={t(field.placeholder)}
                value={selectValues}
                onChange={this.onChange}
                onBlur={onBlur}
                popOverContent={field.validationMessage}
                isPopOverOpen={!!field.validationMessage}
                {...allProps.fieldProps}
                testId={field.testId}
            />
        );
    };

    renderSwitch = ({ disabled, allProps }) => {
        const { field } = this.props;
        return (
            //@others-spread-end
            <Switch
                {...allProps.fieldProps}
                onChange={this.onChange}
                checked={field.value}
                disabled={disabled}
            />
        );
    };

    renderDefaultField = ({ disabled, allProps, onBlur }) => {
        const { field, isEditing, theme, t } = this.props;
        return (
            //@others-spread-end
            <EnhancedInput
                theme={theme}
                disabled={isEditing && disabled}
                fieldId={field.accessor}
                placeholder={t(field.placeholder)}
                error={field.validationMessage}
                value={field.value}
                onChange={this.onChange}
                onBlur={onBlur}
                {...allProps.fieldProps}
                testId={field.testId}
            />
        );
    };

    renderBaseField = () => {
        const { field, disabledOnEdit } = this.props;
        const onBlur = (field?.supportsOnBlur && this.onBlur) || null;

        const allProps = this.getFieldProps({ field });
        const isDisabledOnEdit = _.isFunction(field.disabledOnEdit)
            ? disabledOnEdit({ field })
            : field.disabledOnEdit;
        const isDisabled = allProps.fieldProps?.disabled || isDisabledOnEdit;
        switch (field.type) {
            case FieldTypeEnum.Select:
                return this.renderSelect({
                    disabled: isDisabled,
                    allProps,
                    onBlur
                });

            case FieldTypeEnum.Boolean:
                return this.renderSwitch({
                    disabled: isDisabled,
                    allProps
                });

            default:
                return this.renderDefaultField({
                    disabled: isDisabled,
                    allProps,
                    onBlur
                });
        }
    };

    renderFieldComponent = () => {
        const {
            field,
            isEditing,
            theme,
            disabledOnEdit,
            disabled,
            entity
        } = this.props;

        const onBlur = (field?.supportsOnBlur && this.onBlur) || null;
        const onDelete = (field?.supportsOnDelete && this.onDelete) || null;

        const allProps = this.getFieldProps({ field });
        const isDisabledOnEdit = _.isFunction(field.disabledOnEdit)
            ? disabledOnEdit({ field })
            : field.disabledOnEdit;

        const FieldComponent = field.component || field.formComponent;
        return FieldComponent ? (
            //@others-spread-end
            <FieldComponent
                testId={field.testId}
                field={field}
                disabled={disabled || (isEditing && isDisabledOnEdit)}
                theme={theme}
                onChange={this.onChange}
                onBlur={onBlur}
                onDelete={onDelete}
                entity={entity}
                {...allProps.fieldProps}
            />
        ) : null;
    };

    render() {
        return this.renderFieldComponent() || this.renderBaseField();
    }
}

export default BaseField;
